// Article sections come in three shapes, distinguished by `section_type`: a topic
// section (markdown + optional audio), an images section (photo or slider), or a video
// section (one external link). All live in the same table and share one `position`
// sequence, so staff can drag a photo or video block anywhere between the topic sections
// and readers see exactly that order.

export const SECTION_TYPE_TOPIC = 'topic'
export const SECTION_TYPE_IMAGES = 'images'
export const SECTION_TYPE_VIDEO = 'video'

export type SectionImage = {
  file_id: string
  /** Optional, deliberately understated caption shown under the photo. */
  caption?: string | null
}

export type SectionVideo = {
  /** External video link - the video itself is never uploaded to storage. */
  url: string
  caption?: string | null
  /**
   * Optional still from the media library, shown with a play button on top. Portals that
   * refuse embedding give us no preview frame of their own, so staff pick one.
   */
  thumbnail_file_id?: string | null
}

/** The `section_type` column defaults to 'topic', but older rows read back as null-ish. */
export function isImageSection(section: { section_type?: string | null }): boolean {
  return section.section_type === SECTION_TYPE_IMAGES
}

export function isVideoSection(section: { section_type?: string | null }): boolean {
  return section.section_type === SECTION_TYPE_VIDEO
}

/**
 * Photo and video blocks carry no topic, title or eyebrow. Callers that only care about
 * the "real" text sections (first/last card, article anchor) filter with this.
 */
export function isMediaSection(section: { section_type?: string | null }): boolean {
  return isImageSection(section) || isVideoSection(section)
}

/**
 * `images` is a jsonb column, so it arrives as unknown. Anything that isn't a usable
 * `{ file_id }` entry is dropped rather than rendered as a broken image.
 */
export function parseSectionImages(value: unknown): SectionImage[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const { file_id, caption } = entry as Record<string, unknown>
    if (typeof file_id !== 'string' || !file_id) return []
    return [{ file_id, caption: typeof caption === 'string' && caption.trim() ? caption : null }]
  })
}

/**
 * A video section stores its single link in the same `images` jsonb column - that keeps
 * the new type free of a schema change, and the column is already the place where a
 * section keeps its structured media payload.
 */
export function parseSectionVideo(value: unknown): SectionVideo | null {
  const entry = Array.isArray(value) ? value[0] : value
  if (!entry || typeof entry !== 'object') return null
  const { url, caption, thumbnail_file_id } = entry as Record<string, unknown>
  if (typeof url !== 'string' || !url.trim()) return null
  return {
    url: url.trim(),
    caption: typeof caption === 'string' && caption.trim() ? caption : null,
    thumbnail_file_id:
      typeof thumbnail_file_id === 'string' && thumbnail_file_id.trim() ? thumbnail_file_id : null,
  }
}

/**
 * Every storage file id a section references — die Fotos eines Bild-Abschnitts *und* das
 * Vorschaubild eines Video-Abschnitts. Die Mediathek braucht das, um "wird verwendet" zu
 * beantworten: ohne die Abschnitte sähen Bilder unbenutzt aus, die sehr wohl im Artikel
 * stehen, und würden beim Aufräumen gelöscht.
 */
export function collectSectionFileIds(images: unknown): string[] {
  const entries = Array.isArray(images) ? images : [images]
  return entries.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const { file_id, thumbnail_file_id } = entry as Record<string, unknown>
    const ids: string[] = []
    if (typeof file_id === 'string' && file_id) ids.push(file_id)
    if (typeof thumbnail_file_id === 'string' && thumbnail_file_id) ids.push(thumbnail_file_id)
    return ids
  })
}

/**
 * How a given link can be shown. Most portals (the WHO's Canto library among them) send
 * `X-Frame-Options: sameorigin`, so an iframe would render an empty box - those fall back
 * to `link` and get a card that opens in a new tab instead of a silently broken player.
 */
export type VideoEmbed =
  { kind: 'iframe'; src: string } | { kind: 'file'; src: string } | { kind: 'link'; src: string }

const VIDEO_FILE_EXTENSIONS = ['.mp4', '.webm', '.ogv', '.ogg', '.mov']

export function resolveVideoEmbed(rawUrl: string): VideoEmbed {
  const url = rawUrl.trim()
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return { kind: 'link', src: url }
  }

  const host = parsed.hostname.replace(/^www\./, '')
  const path = parsed.pathname

  if (host === 'youtu.be') {
    const id = path.slice(1)
    if (id) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${id}` }
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
    const id = parsed.searchParams.get('v') || path.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1]
    if (id) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${id}` }
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = path.match(/(?:^|\/)(\d+)/)?.[1]
    if (id) return { kind: 'iframe', src: `https://player.vimeo.com/video/${id}` }
  }

  if (VIDEO_FILE_EXTENSIONS.some((ext) => path.toLowerCase().endsWith(ext))) {
    return { kind: 'file', src: url }
  }

  return { kind: 'link', src: url }
}

/** Short, readable source label for the fallback card, e.g. "who.canto.global". */
export function videoLinkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
